import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import apolloClient from '../../apollo-client'
import { GET_COMPANIES_QUERY } from '../../graphql/company'
import { ICompanyListItem } from '../../types/Company'
import DefaultHero from '../../public/assets/img/company/default.webp'
import PageHeader from '../../components/pageHeader/PageHeader'
import Card from '../../components/ui/card/Card'
import CompanyFilters, {
  CompaniesFilterRefType
} from '../../components/filters/CompanyFilters'
import InfiniteScroll from 'react-infinite-scroll-component'
import { shuffleArray } from '../../lib/Utils'
import CompanyListHead from '../../components/heads/CompanyListHead'
import { LoadMask } from '../../components/ui/LoadMask'
import LeadAcquisitionModal from '../../components/modals/LeadAcquisitionModal'
import LeadContext from '../../contexts/LeadContext'
import NoResult from '../../components/ui/NoResult'
import GlobalContext from '../../contexts/GlobalContext'

const LIMIT = 9
const STARED_CLIENTS_MIN_WEIGHT = 0.9

export default function CompanySearch() {
  const [clients, setClients] = useState<ICompanyListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [totalCnt, setTotalCnt] = useState(0)
  const [showLeadAcquisition, setShowLeadAcquisition] = useState(false)
  const { setLeadFormStaticData } = useContext(LeadContext)

  const filterRef = useRef<CompaniesFilterRefType>(null)

  const initLoad = useRef(false)
  const { setErrorMsg } = useContext(GlobalContext)

  const pageSize = useRef(0)

  const filterClients = useCallback(
    async (reset = false) => {
      let excludeIds: string[] = []
      try {
        if (reset) {
          setClients([])
          pageSize.current = 0
        } else {
          excludeIds = clients.map((item) => item._id)
        }
        setLoading(true)

        const { data } = await apolloClient.query({
          query: GET_COMPANIES_QUERY,
          fetchPolicy: 'network-only',
          variables: {
            query: {
              ...(filterRef.current?.getValues() || {}),
              skip: pageSize.current,
              limit: LIMIT,
              excludeIds
            }
          }
        })

        const newItems = data.clientsByJobPostFieldsV2
          .items as ICompanyListItem[]

        const newData = shuffleArray(
          newItems.filter(
            (item) => item.campaignPackageWeight >= STARED_CLIENTS_MIN_WEIGHT
          )
        ).concat(
          shuffleArray(
            newItems.filter(
              (item) =>
                !item.campaignPackageWeight ||
                item.campaignPackageWeight < STARED_CLIENTS_MIN_WEIGHT
            )
          )
        )

        if (reset) {
          setClients(() => newData)
        } else {
          setClients((oldClients) => [...oldClients, ...newData])
        }

        setTotalCnt(
          newItems.length < data.clientsByJobPostFieldsV2.totalCount
            ? data.clientsByJobPostFieldsV2.totalCount
            : 0
        )
        pageSize.current = pageSize.current + LIMIT
      } catch (e) {
        setErrorMsg('Uh-oh! Error loading companies. Try again soon.')
      } finally {
        setLoading(false)
      }
    },
    [setErrorMsg, clients]
  )

  const onClear = () => {
    filterClients(true)
  }

  useEffect(() => {
    if (!initLoad.current) {
      filterClients(true)
      initLoad.current = true
    }
  }, [filterClients])

  const onApply = (client: ICompanyListItem) => {
    // just for additional insurance
    setLeadFormStaticData({
      clientId: client._id,
      campaignId: client.defaultCampaign._id,
      clientName: client.name,
      heroImage: client.resources?.hero?.Location
    })
    setShowLeadAcquisition(true)
  }

  return (
    <>
      <CompanyListHead />
      <PageHeader
        bucket="trucking-companies/hero"
        defaultS3FileName="default"
        isHold={false}
      />

      <CompanyFilters
        ref={filterRef}
        onSearch={() => {
          filterClients(true)
        }}
        onClear={onClear}
      />

      <InfiniteScroll
        scrollableTarget="scrollableTarget"
        dataLength={clients.length}
        loader={null}
        style={{ position: 'relative' }}
        next={() => {
          filterClients()
        }}
        hasMore={totalCnt > 0}>
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
          <LoadMask active={loading} />
          {clients.map((client, i) => {
            return (
              <Card
                starBadge={
                  client?.campaignPackageWeight >= STARED_CLIENTS_MIN_WEIGHT
                }
                image={client?.resources?.hero?.Location || DefaultHero}
                title={client.name}
                key={i}
                overview={client.defaultJobPost?.overview}
                phoneNumber={
                  client.trackingNumbers?.[0]?.trackingNumber ||
                  client.trackingNumbers?.[0]?.routingNumber
                }
                href={`/trucking-companies/${client.slug}`}
                hideFooter={false}
                onApplyNow={() => onApply(client)}></Card>
            )
          })}
        </div>
      </InfiniteScroll>
      <div>
        <NoResult visible={!loading && !clients.length} />
      </div>
      {showLeadAcquisition && (
        <LeadAcquisitionModal
          open={showLeadAcquisition}
          handleClose={setShowLeadAcquisition}
        />
      )}
    </>
  )
}
